export interface DungeonMusicStep {
  readonly bass?: number;
  readonly lead?: number;
  readonly accent?: boolean;
}

// Original 8-bit underground groove: short notes and deliberate rests keep it
// playful and propulsive without reproducing a melody from an existing game.
export const DUNGEON_MUSIC_STEPS: readonly DungeonMusicStep[] = [
  { bass: 65.41, accent: true },
  {},
  { bass: 65.41, lead: 261.63 },
  { lead: 311.13 },
  { bass: 77.78 },
  {},
  { bass: 98, lead: 392, accent: true },
  { lead: 349.23 },

  { bass: 65.41, accent: true },
  {},
  { bass: 77.78, lead: 311.13 },
  { lead: 261.63 },
  { bass: 87.31 },
  { lead: 293.66 },
  { bass: 98, lead: 392 },
  {},

  { bass: 58.27, accent: true },
  {},
  { bass: 69.3, lead: 349.23 },
  { lead: 415.3 },
  { bass: 77.78 },
  {},
  { bass: 103.83, lead: 466.16, accent: true },
  { lead: 415.3 },

  { bass: 58.27, accent: true },
  { lead: 293.66 },
  { bass: 69.3, lead: 349.23 },
  {},
  { bass: 61.74, lead: 311.13 },
  { lead: 277.18 },
  { bass: 65.41, lead: 261.63, accent: true },
  {},
] as const;

const STEP_SECONDS = 60 / 120 / 2;
const SCHEDULE_AHEAD_SECONDS = 0.35;

export class DungeonMusicSequencer {
  private enabled = false;
  private nextStepTime = 0;
  private stepIndex = 0;
  private readonly activeOscillators = new Set<OscillatorNode>();

  public constructor(
    private readonly context: AudioContext,
    private readonly output: AudioNode,
  ) {}

  public setEnabled(enabled: boolean): void {
    if (enabled === this.enabled) return;
    this.enabled = enabled;
    this.stepIndex = 0;
    this.nextStepTime = this.context.currentTime + 0.08;
    if (!enabled) this.stopActiveVoices();
  }

  public update(): void {
    if (!this.enabled || this.context.state !== "running") return;
    if (this.nextStepTime < this.context.currentTime - STEP_SECONDS) {
      this.nextStepTime = this.context.currentTime + 0.03;
    }
    const scheduleUntil = this.context.currentTime + SCHEDULE_AHEAD_SECONDS;
    while (this.nextStepTime <= scheduleUntil) {
      this.scheduleStep(DUNGEON_MUSIC_STEPS[this.stepIndex] as DungeonMusicStep);
      this.stepIndex = (this.stepIndex + 1) % DUNGEON_MUSIC_STEPS.length;
      this.nextStepTime += STEP_SECONDS;
    }
  }

  public dispose(): void {
    this.enabled = false;
    this.stopActiveVoices();
  }

  private scheduleStep(step: DungeonMusicStep): void {
    const start = this.nextStepTime;
    if (step.bass) {
      this.scheduleVoice(
        step.bass,
        start,
        STEP_SECONDS * 0.68,
        "square",
        step.accent ? 0.052 : 0.04,
        330,
      );
      this.scheduleVoice(step.bass / 2, start, STEP_SECONDS * 0.52, "triangle", 0.038, 190);
    }
    if (step.lead) {
      this.scheduleVoice(
        step.lead,
        start + STEP_SECONDS * 0.06,
        STEP_SECONDS * 0.58,
        "square",
        step.accent ? 0.026 : 0.02,
        1250,
      );
    }
  }

  private scheduleVoice(
    frequency: number,
    start: number,
    duration: number,
    wave: OscillatorType,
    volume: number,
    filterFrequency: number,
  ): void {
    const oscillator = this.context.createOscillator();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    oscillator.type = wave;
    oscillator.frequency.setValueAtTime(frequency, start);
    oscillator.detune.setValueAtTime(-3, start);
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(filterFrequency, start);
    filter.Q.value = 1.2;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(filter).connect(gain).connect(this.output);
    this.activeOscillators.add(oscillator);
    oscillator.addEventListener("ended", () => {
      this.activeOscillators.delete(oscillator);
      oscillator.disconnect();
      filter.disconnect();
      gain.disconnect();
    });
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  private stopActiveVoices(): void {
    for (const oscillator of this.activeOscillators) {
      try {
        oscillator.stop();
      } catch {
        // A voice may already have reached its scheduled stop time.
      }
    }
    this.activeOscillators.clear();
  }
}
